import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Delete, DragIndicator } from "@mui/icons-material";
import { IconButton, TextField } from "@mui/material";
import { FC, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { AreaWaypointBadgeStyled } from "@ryde/controls";
import { IAreaWaypoint } from "@ryde/shared";

interface IEditAreaWaypointsRowProps {
  waypoint: IAreaWaypoint;
  disabled?: boolean;
  onNameChange: ({ id, name }: { id: number; name: string }) => void;
  onDelete: (id: number) => void;
}

export const EditAreaWaypointsRow: FC<IEditAreaWaypointsRowProps> = ({
  waypoint,
  disabled,
  onNameChange,
  onDelete,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(waypoint.id), disabled });

  const handleNameChange = useEvent((event: ChangeEvent<HTMLInputElement>) => {
    onNameChange({ id: waypoint.id, name: event.target.value });
  });

  return (
    <div
      ref={setNodeRef}
      className={`edit-area-waypoints-row${isDragging ? " is-dragging" : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <span className="edit-area-waypoints-grip" {...attributes} {...listeners}>
        <DragIndicator fontSize="small" />
      </span>
      <AreaWaypointBadgeStyled>w</AreaWaypointBadgeStyled>
      <TextField
        className="edit-area-waypoints-name-field"
        size="small"
        value={waypoint.name}
        disabled={disabled}
        onChange={handleNameChange}
        label={t("stationRoute.waypoint.CUSTOM_NAME_LABEL")}
        placeholder={
          waypoint.fullAddress || t("stationRoute.waypoint.DEFAULT_NAME")
        }
      />
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={t("general.DELETE")}
        onClick={() => onDelete(waypoint.id)}
      >
        <Delete fontSize="small" />
      </IconButton>
    </div>
  );
};
